import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow } from './AcademyShared';

const STATUSES = ['scheduled', 'completed', 'cancelled', 'rescheduled'];

export default function AcademyCalendar() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/sessions?per_page=100`;
      if (status) url += `&status=${status}`;
      if (cohortFilter) url += `&cohort_id=${cohortFilter}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, cohortFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=500&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setCohorts(Array.isArray(j.data) ? j.data : [])).catch(() => setCohorts([]));
    fetch(`${API}/companies/${companyId}/academy/instructors?per_page=500&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setInstructors(Array.isArray(j.data) ? j.data : [])).catch(() => setInstructors([]));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    const res = await fetch(`${API}/companies/${companyId}/academy/sessions/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Status change failed'); return; }
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/sessions/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🗓️ Calendar / Sessions</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Session</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 220 }} value={cohortFilter} onChange={e => setCohortFilter(e.target.value)}>
          <option value="">All Cohorts</option>
          {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Title', 'Cohort', 'Instructor', 'Date', 'Time', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No sessions</td></tr>}
              {list.map(s => (
                <tr key={s.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{s.title}</td>
                  <td style={tdStyle}>{s.cohort?.name || '—'}</td>
                  <td style={tdStyle}>{s.instructor?.display_name || '—'}</td>
                  <td style={tdStyle}>{s.session_date}</td>
                  <td style={tdStyle}>{s.start_time} - {s.end_time}</td>
                  <td style={tdStyle}><Badge value={s.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(s)}>Edit</button>
                    {s.status === 'scheduled' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(s.id, 'completed')}>Complete</button>}
                    {s.status === 'scheduled' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(s.id, 'cancelled')}>Cancel</button>}
                    {s.status === 'scheduled' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(s.id, 'rescheduled')}>Reschedule</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <SessionModal companyId={companyId} cohorts={cohorts} instructors={instructors} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
    </div>
  );
}

function SessionModal({ companyId, cohorts, instructors, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    cohort_id: item.cohort_id || '', level_id: item.level_id || '', instructor_id: item.instructor_id || '',
    title: item.title || '', session_date: item.session_date || '', start_time: item.start_time || '',
    end_time: item.end_time || '', location: item.location || '', notes: item.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [levels, setLevels] = useState([]);

  useEffect(() => {
    if (!form.cohort_id) { setLevels([]); return; }
    fetch(`${API}/companies/${companyId}/academy/cohorts/${form.cohort_id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { cohort: null })
      .then(j => setLevels(j.cohort?.levels || []));
  }, [companyId, form.cohort_id]);

  const save = async () => {
    if (!form.title.trim() || !form.cohort_id || !form.session_date) { alert('Title, Cohort and Date are required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/sessions` : `${API}/companies/${companyId}/academy/sessions/${item.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Session' : 'Edit Session'}</h3>
        <FormRow label="Title *"><input style={inputStyle} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Cohort *">
            <select style={inputStyle} value={form.cohort_id} onChange={e => setForm({ ...form, cohort_id: e.target.value })}>
              <option value="">Select cohort</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FormRow>
          <FormRow label="Instructor">
            <select style={inputStyle} value={form.instructor_id} onChange={e => setForm({ ...form, instructor_id: e.target.value })}>
              <option value="">Select instructor</option>
              {instructors.map(i => <option key={i.id} value={i.id}>{i.display_name}</option>)}
            </select>
          </FormRow>
        </div>
        <FormRow label="Level (optional)">
          <select style={inputStyle} value={form.level_id} onChange={e => setForm({ ...form, level_id: e.target.value })} disabled={!form.cohort_id || levels.length === 0}>
            <option value="">{form.cohort_id ? (levels.length === 0 ? 'No levels for this cohort' : 'Select level') : 'Select a cohort first'}</option>
            {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </FormRow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <FormRow label="Date *"><input type="date" style={inputStyle} value={form.session_date} onChange={e => setForm({ ...form, session_date: e.target.value })} /></FormRow>
          <FormRow label="Start Time"><input type="time" style={inputStyle} value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></FormRow>
          <FormRow label="End Time"><input type="time" style={inputStyle} value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Location"><input style={inputStyle} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></FormRow>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
